import { useRef, useState } from 'react';

/**
 * Reference images for the Gemini generators (Evan 2026-10-27: "anywhere in admin dash where I
 * am using image generator, I need to be able to upload reference images easily").
 *
 * One small control used by BOTH the events hero generator and the studio's Image/Backdrop box:
 * a "+ Ref" button → file picker (multiple) → each image is downscaled to ≤1024px JPEG in the
 * browser (keeps the function payload small) and shown as a removable thumbnail. The payload
 * shape {mime, data} is exactly what studio-image forwards to Gemini as inline parts (max 6).
 */

export type RefImage = { mime: string; data: string; name: string };

export async function fileToRef(file: File): Promise<RefImage> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Not an image'));
    i.src = dataUrl;
  });
  const MAX = 1024;
  const scale = Math.min(1, MAX / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width * scale));
  const h = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
  const jpeg = canvas.toDataURL('image/jpeg', 0.85);
  return { mime: 'image/jpeg', data: jpeg.slice(jpeg.indexOf(',') + 1), name: file.name };
}

export function RefImagePicker({
  refs,
  setRefs,
  disabled,
  max = 6,
}: {
  refs: RefImage[];
  setRefs: (next: RefImage[]) => void;
  disabled?: boolean;
  /** 6 by default (Evan 2026-10-28: "make it possible to add more reference images as I go"). */
  max?: number;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const addFiles = (files: File[]) => {
    const imgs = files.filter((f) => f.type.startsWith('image/')).slice(0, Math.max(0, max - refs.length));
    if (imgs.length === 0) return;
    void Promise.all(imgs.map(fileToRef)).then(
      (added) => setRefs([...refs, ...added]),
      () => {
        /* unreadable file — ignore */
      },
    );
  };
  return (
    <span
      className={`inline-flex flex-wrap items-center gap-1.5 rounded-lg transition-shadow ${
        dragOver ? 'ring-1 ring-accent' : ''
      }`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (!disabled) addFiles(Array.from(e.dataTransfer.files));
      }}
    >
      {refs.map((r, i) => (
        <span key={`${r.name}-${i}`} className="relative inline-block">
          <img
            src={`data:${r.mime};base64,${r.data}`}
            alt={r.name}
            title={r.name}
            className="h-9 w-9 rounded-md border border-border object-cover"
          />
          <button
            type="button"
            aria-label={`Remove ${r.name}`}
            onClick={() => setRefs(refs.filter((_, j) => j !== i))}
            className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-border bg-black text-[9px] leading-none text-white hover:bg-zinc-800"
          >
            ✕
          </button>
        </span>
      ))}
      {refs.length < max ? (
        <>
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className="rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] text-mutedDim hover:text-white disabled:opacity-50"
            title={`Attach up to ${max} reference images (cmd-click to pick several) — Gemini uses them for style/mood/composition`}
          >
            + Ref image
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []).slice(0, max - refs.length);
              e.target.value = '';
              void Promise.all(files.map(fileToRef)).then(
                (added) => setRefs([...refs, ...added]),
                () => {
                  /* unreadable file — ignore */
                },
              );
            }}
          />
        </>
      ) : null}
    </span>
  );
}
